<script setup lang="ts">
/**
 * The selected span's captured payload.
 *
 * Reached only through the trace: `GET resources/traces/{traceId}/requests/{requestId}` is the sole
 * route to a request body, so a payload always arrives with the context that produced it.
 */
import { computed, ref, watch } from 'vue'
import type { RequestDetail, TraceSpan } from '@/backend/traces.types'
import useTracesViewContext from './use-traces-view-context'
import HtmlPreview from './HtmlPreview.vue'
import { NavigationBar, NavItemDefinition } from '@hantera/design-system';
import { formatting } from '@hantera/portal-app';

const props = defineProps<{
  span: TraceSpan
}>()

const traces = useTracesViewContext()

type PayloadTab = 'request' | 'response'

/**
 * Job and sending tabs.
 *
 * Kept as this component's own tab strip rather than the design system's `TabPanel`: that takes a
 * component per tab, which would mean six new files for what are four `<pre>` blocks, and it keeps
 * every tab mounted — the opposite of what the HTML preview needs.
 */
type SpanTab = 'parameters' | 'result' | 'details' | 'html' | 'text' | 'source'

const activeTab = ref<PayloadTab>('request')
const activeSpanTab = ref<SpanTab>('details')
const exchangeIndex = ref(0)

/**
 * The request id is the span id, for spans that have a stored row.
 *
 * A synthesized node has no `spanId`, and neither do job or sending spans — their rows are not
 * captured requests. Those render the metadata they carry and nothing more.
 */
const requestId = computed(() =>
  props.span.kind === 'ingress' || props.span.kind === 'egress' ? props.span.spanId : undefined,
)

const detail = computed<RequestDetail | null | undefined>(() =>
  requestId.value ? traces.requestDetail(requestId.value) : undefined,
)

const isLoading = computed(() =>
  requestId.value ? traces.isLoadingRequest(requestId.value) : false,
)


/**
 * Distinguishes "not applicable" from "asked, and there was nothing".
 *
 * A null cache entry is the second: the payload may have expired, been logged at `off`, or lie
 * outside the caller's permissions. The API deliberately does not say which, so one notice covers
 * all three.
 */
const unavailable = computed(() => requestId.value != null && detail.value === null)

/**
 * The exchange picker is an egress affordance: it separates a token fetch from the call that
 * followed. An ingress receives one request, so there is nothing to pick between.
 */
const isIngress = computed(() => detail.value?.direction === 'ingress')

const exchanges = computed(() => detail.value?.exchanges ?? [])

const exchange = computed(() => exchanges.value[exchangeIndex.value])

/** `{operation} {target}` off the row for an ingress, off the selected exchange for an egress. */
const target = computed(() =>
  isIngress.value
    ? `${detail.value?.operation ?? ''} ${detail.value?.target ?? ''}`.trim()
    : `${exchange.value?.operation ?? ''} ${exchange.value?.target ?? ''}`.trim(),
)

const metadata = computed(() => {
  // Both halves come off the row for an ingress: there is one request, and one set of response
  // headers that a stream sets when it opens and never changes.
  const source = isIngress.value
    ? activeTab.value === 'request'
      ? detail.value?.requestMetadata
      : detail.value?.responseMetadata
    : activeTab.value === 'request'
      ? exchange.value?.requestMetadata
      : exchange.value?.responseMetadata

  return Object.entries(source ?? {})
})

const body = computed(() => {
  if (isIngress.value) {
    return activeTab.value === 'request' ? detail.value?.requestBody : undefined
  }

  return activeTab.value === 'request' ? exchange.value?.requestBody : exchange.value?.responseBody
})

const bodyTruncated = computed(() => {
  if (isIngress.value) {
    return activeTab.value === 'request' ? detail.value?.requestBodyTruncated : false
  }

  return activeTab.value === 'request'
    ? exchange.value?.requestBodyTruncated
    : exchange.value?.responseBodyTruncated
})

const responseFrames = computed(() => detail.value?.responseFrames ?? [])

/** Grows while a stream is open, so it is reported rather than inferred from what has arrived. */
const frameCount = computed(() => detail.value?.frameCount ?? 0)

const hasMoreFrames = computed(() => frameCount.value > responseFrames.value.length)

/**
 * Which frame's payload is expanded. Frames are collapsed by default: a stream's list is the point,
 * and dumping every payload inline would bury it.
 */
const openFrame = ref<number | null>(null)

function toggleFrame(sequence: number): void {
  openFrame.value = openFrame.value === sequence ? null : sequence
}

async function loadMoreFrames(): Promise<void> {
  if (requestId.value) {
    await traces.loadMoreFrames(props.span.traceId, requestId.value)
  }
}

const failed = computed(() => detail.value?.success === false || props.span.success === false)

const errorMessage = computed(
  () => detail.value?.errorMessage ?? sending.value?.errorMessage ?? props.span.errorMessage,
)

/**
 * A job or sending span has a durable row rather than a captured request, so its payload is fetched
 * separately and exists regardless of any log level.
 */
const payloadSpanId = computed(() =>
  props.span.kind === 'job' || props.span.kind === 'sending' ? props.span.spanId : undefined,
)

const job = computed(() =>
  props.span.kind === 'job' && props.span.spanId
    ? traces.jobSpanDetail(props.span.spanId)
    : undefined,
)

const sending = computed(() =>
  props.span.kind === 'sending' && props.span.spanId
    ? traces.sendingSpanDetail(props.span.spanId)
    : undefined,
)

const isLoadingPayload = computed(() =>
  payloadSpanId.value ? traces.isLoadingSpan(payloadSpanId.value) : false,
)

/** @see unavailable — a null cache entry means asked, and there was nothing. */
const payloadUnavailable = computed(
  () =>
    payloadSpanId.value != null &&
    (props.span.kind === 'job' ? job.value === null : sending.value === null),
)

const sendingData = computed(() => sending.value?.data ?? {})

function sendingField(key: string): string | undefined {
  const value = sendingData.value[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

const bodyHtml = computed(() => sendingField('bodyHtml'))
const bodyText = computed(() => sendingField('bodyText'))
const subject = computed(() => sendingField('subject'))

/**
 * Addressing and the like, without the two bodies.
 *
 * They are excluded because they have tabs of their own — repeating a whole HTML document in a
 * key/value table would bury every other field.
 */
const sendingFields = computed(() =>
  Object.entries(sendingData.value).filter(([key]) => key !== 'bodyHtml' && key !== 'bodyText'),
)

/** Tabs are per kind, and only offered when the underlying value exists. */
const spanTabs = computed<NavItemDefinition[]>(() => {
  if (props.span.kind === 'job') {
    return [
      { id: 'parameters', label: 'Parameters' },
      { id: 'result', label: 'Result' },
    ]
  }

  if (props.span.kind === 'sending') {
    const tabs: { id: SpanTab; label: string }[] = [{ id: 'details', label: 'Details' }]

    if (bodyHtml.value) tabs.push({ id: 'html', label: 'HTML' })
    if (bodyHtml.value) tabs.push({ id: 'source', label: 'Source' })
    if (bodyText.value) tabs.push({ id: 'text', label: 'Text' })

    return tabs
  }

  return []
})

watch(
  () => [props.span.spanKey, requestId.value, payloadSpanId.value],
  () => {
    exchangeIndex.value = 0
    activeTab.value = 'request'
    openFrame.value = null

    // Never `html`: a body must not render until the operator asks for it by name.
    activeSpanTab.value = props.span.kind === 'job' ? 'parameters' : 'details'

    if (requestId.value) {
      traces.loadRequest(props.span.traceId, requestId.value)
    }

    if (payloadSpanId.value) {
      if (props.span.kind === 'job') {
        traces.loadJobSpan(props.span.traceId, payloadSpanId.value)
      } else {
        traces.loadSendingSpan(props.span.traceId, payloadSpanId.value)
      }
    }
  },
  { immediate: true },
)

function formatDuration(ms?: number): string {
  if (ms == null) return '—'
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`
}

/** Pretty-prints JSON when it is JSON, and leaves anything else exactly as captured. */
function formatBody(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

/**
 * Renders an already-parsed value, which is what a job result or sending field is — the transport
 * decoded it, so unlike `formatBody` there is no JSON text to re-parse.
 *
 * Strings pass through unquoted; everything else is pretty-printed. A job's result is untyped, so this
 * has to cope with a scalar as readily as an object.
 */
function formatValue(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'string') return value

  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
</script>

<template>
  <div class="span-detail box-content p">
    <header>
      <h3>{{ span.label || span.resourceId }}</h3>
      <span v-if="span.status" class="status" :class="{ failed }">{{ span.status }}</span>
    </header>

    <div class="stats">
      <div class="stat">
        <span class="stat-label">Duration</span>
        <span>{{ formatDuration(detail?.durationMs ?? span.durationMs) }}</span>
      </div>
      <div class="stat">
        <span class="stat-label">Started</span>
        <span>{{ formatting.dateTime(detail?.startedAt ?? span.startedAt ?? new Date()) }}</span>
      </div>
      <div v-if="detail" class="stat">
        <span class="stat-label">Level</span>
        <span>{{ detail.level }}</span>
      </div>
      <div v-if="span.recipient" class="stat">
        <span class="stat-label">Recipient</span>
        <span>{{ span.recipient }}</span>
      </div>
      <div v-if="detail?.preview" class="stat">
        <span class="stat-label">Preview</span>
        <span>yes</span>
      </div>
    </div>

    <p v-if="errorMessage" class="error-banner">{{ errorMessage }}</p>

    <p v-if="isLoading" class="notice">Loading request detail…</p>

    <p v-else-if="unavailable" class="notice">
      Request detail is not available. It may have expired, been logged at level 'off', or lie
      outside your permissions.
    </p>

    <!-- Job and sending spans: a durable payload rather than a captured request. -->
    <template v-else-if="payloadSpanId">
      <p v-if="isLoadingPayload" class="notice">Loading payload…</p>

      <p v-else-if="payloadUnavailable" class="notice">
        Payload is not available. It may have been deleted, or lie outside your permissions.
      </p>

      <template v-else>

        <NavigationBar
          :items="spanTabs"
          v-model="activeSpanTab"
        />

        <template v-if="span.kind === 'job'">
          <pre v-if="activeSpanTab === 'parameters'" class="body">{{
            job?.parameters && Object.keys(job.parameters).length > 0
              ? formatValue(job.parameters)
              : 'This job was scheduled without parameters.'
          }}</pre>

          <pre v-else class="body">{{
            job?.result != null ? formatValue(job.result) : 'This job returned no result.'
          }}</pre>
        </template>

        <template v-else>
          <template v-if="activeSpanTab === 'details'">
            <p v-if="subject" class="target" :title="subject">{{ subject }}</p>

            <table v-if="sendingFields.length > 0" class="metadata">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="[key, value] in sendingFields" :key="key">
                  <td>{{ key }}</td>
                  <td>{{ formatValue(value) }}</td>
                </tr>
              </tbody>
            </table>

            <p v-else class="notice">No payload was recorded for this sending.</p>
          </template>

          <!-- Mounted only while selected, so no document is built for a tab nobody opened. -->
          <HtmlPreview
            v-else-if="activeSpanTab === 'html' && bodyHtml"
            :html="bodyHtml"
            :active="activeSpanTab === 'html'"
          />

          <pre v-else-if="activeSpanTab === 'text'" class="body">{{ bodyText }}</pre>

          <pre v-else class="body">{{ bodyHtml }}</pre>
        </template>
      </template>
    </template>

    <p v-else-if="!requestId" class="notice">
      This span has no captured payload. Rules and actors are not individually recorded.
    </p>

    <template v-else-if="isIngress || exchanges.length > 0">
      <!-- Egress only: one call can make several round trips (a token fetch, then the real request),
           so the exchange is selectable. An ingress has exactly one request by construction. -->
      <div v-if="!isIngress && exchanges.length > 1" class="exchange-picker">
        <button
          v-for="(item, index) in exchanges"
          :key="index"
          class="exchange-tab btn-reset"
          :class="{ active: index === exchangeIndex }"
          type="button"
          @click="exchangeIndex = index"
        >
          {{ item.operation }} {{ index + 1 }}
        </button>
      </div>

      <p class="target" :title="target">{{ target }}</p>

      <NavigationBar
        :items="[{
          id: 'request',
          label: 'Request'
        }, {
          id: 'response',
          label: 'Response'
        }]"
        v-model="activeTab"
      />

      <table v-if="metadata.length > 0" class="metadata">
        <thead>
          <tr>
            <th>Header</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="[key, value] in metadata" :key="key">
            <td>{{ key }}</td>
            <td>{{ value }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Frames carry only what varies per event, so they sit below the response headers rather than
           replacing them. A stream's list is the point; an ordinary response has at most one. -->
      <template v-if="isIngress && activeTab === 'response'">
        <template v-if="responseFrames.length > 0">
          <div class="frames">
            <div v-for="frame in responseFrames" :key="frame.sequence" class="frame">
              <button class="frame-header btn-reset" type="button" @click="toggleFrame(frame.sequence)">
                <span class="frame-sequence">#{{ frame.sequence }}</span>
                <span class="frame-name">{{ frame.name ?? 'response' }}</span>
                <!-- Time only: every frame shares the span's date, shown above. -->
                <span class="frame-time">{{ formatting.time(frame.occurredAt) }}</span>
              </button>

              <template v-if="openFrame === frame.sequence">
                <pre v-if="frame.payload" class="body">{{ formatBody(frame.payload) }}</pre>
                <p v-else class="notice">
                  No payload captured. Payloads are stored only at level 'full'.
                </p>
                <p v-if="frame.payloadTruncated" class="notice">
                  Payload was truncated at the configured size cap.
                </p>
              </template>
            </div>
          </div>

          <!-- A stream's row is written when the connection opens, so more frames can arrive after
               this panel was first opened. -->
          <button v-if="hasMoreFrames" class="load-more btn-reset" type="button" @click="loadMoreFrames">
            Load more ({{ responseFrames.length }} of {{ frameCount }})
          </button>
        </template>

        <p v-else class="notice">
          No response payload was captured. Payloads are stored only at level 'full'.
        </p>
      </template>

      <template v-else-if="body">
        <pre class="body">{{ formatBody(body) }}</pre>
        <p v-if="bodyTruncated" class="notice">Body was truncated at the configured size cap.</p>
      </template>
      <p v-else class="notice">
        No body captured. Bodies are stored only at level 'full'.
      </p>
    </template>

    <p v-else class="notice">No exchanges were recorded for this request.</p>
  </div>
</template>

<style scoped>
.span-detail {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

header {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status {
  font-size: 12px;
  color: hsl(var(--color-40));
}

.status.failed {
  color: hsl(var(--color-hue-red) 60% 45%);
}

.stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
}

.stat {
  display: flex;
  flex-direction: column;
}

.stat-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: hsl(var(--color-50));
}

.error-banner {
  margin: 0;
  padding: 6px 8px;
  border-radius: 4px;
  background-color: hsl(var(--color-hue-red) 70% 95%);
  color: hsl(var(--color-hue-red) 60% 35%);
  font-size: 12px;
}

.notice {
  margin: 0;
  font-size: 12px;
  color: hsl(var(--color-40));
}

.target {
  margin: 0;
  font-family: monospace;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: hsl(var(--color-40));
}

.exchange-picker {
  display: flex;
  gap: 4px;
}

.exchange-tab {
  padding: 3px 8px;
  border: 1px solid hsl(var(--color-70));
  border-radius: 3px;
  background-color: hsl(var(--color-95));
  font-size: 11px;
  cursor: pointer;
}

.exchange-tab.active {
  background-color: hsl(var(--color-100));
  border-color: hsl(var(--color-40));
}

.metadata {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.metadata th {
  text-align: left;
  font-weight: 500;
  color: hsl(var(--color-40));
}

.metadata th,
.metadata td {
  padding: 2px 6px;
  border-bottom: 1px solid hsl(var(--color-90));
  vertical-align: top;
  word-break: break-all;
}

.metadata td:first-child {
  width: 25%;
  font-family: monospace;
}

.body {
  margin: 0;
  padding: 8px;
  max-height: 320px;
  overflow: auto;
  background-color: hsl(var(--color-95));
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Scrolls rather than grows: a stream can hold hundreds of frames without pushing the request
   details off the top of the panel. */
.frames {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 360px;
  overflow: auto;
}

.frame {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.frame-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 3px 6px;
  border-radius: 3px;
  background-color: hsl(var(--color-95));
  font-size: 11px;
  cursor: pointer;
  text-align: left;
}

.frame-header:hover {
  background-color: hsl(var(--color-90));
}

.frame-sequence {
  font-family: monospace;
  color: hsl(var(--color-50));
}

.frame-name {
  flex: 1;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.frame-time {
  font-variant-numeric: tabular-nums;
  color: hsl(var(--color-50));
}

.load-more {
  align-self: flex-start;
  padding: 3px 8px;
  border: 1px solid hsl(var(--color-70));
  border-radius: 3px;
  background-color: hsl(var(--color-95));
  font-size: 11px;
  cursor: pointer;
}
</style>
